<template>
	<div class="form-container">
		<CRow>
			<CCol md="12">
				<h2 class="typo-h4 position-relative">
					e-Coupon setting
				</h2>
			</CCol>
		</CRow>

		<!-- 7Club+ coupon prefix -->
		<CRow class="form-group mt-4 align-items-center">
			<CCol>
				<div class="label">
					7Club+ coupon prefix
				</div>
				<div class="typo-caption color-black-45">
					โค้ดคูปองของโปรโมชันนี้ถูกสร้างจากหลังบ้าน CRM ไม่ได้สร้างในหน้านี้ —
					เปิดแล้วกรอก prefix ที่ใช้จับคู่ว่าคูปองใบไหนเป็นของโปรโมชันนี้
				</div>
			</CCol>
			<CCol md="3">
				<div class="form-group-switch">
					<CSwitch
						:checked.sync="formData.isSevenClubCoupon"
						:disabled="isDisabledSevenClubCouponToggle"
						variant="3d"
						size="sm"
						class="switch-custom"
						color="success"
						data-test-id="seven-club-prefix-switch"
					/>
					<span class="typo-body-2">
						{{ formData.isSevenClubCoupon ? 'On' : 'Off' }}
					</span>
				</div>
			</CCol>
			<CCol v-if="isEditMode" md="12">
				<p class="typo-caption color-black-45 mt-2 mb-0">
					เปิด/ปิดได้เฉพาะตอนสร้างโปรโมชันใหม่ — สลับทีหลังจะทำให้คูปองที่ผูกไว้แล้วจับคู่ไม่ตรง
				</p>
			</CCol>
			<CCol v-if="formData.isSevenClubCoupon" md="12" class="mt-3">
				<div class="bg-gray-100 p-4">
					<CRow>
						<CCol md="12">
							<label>Prefix*</label>
							<div class="prefix-row d-flex align-items-start">
								<CInput
									v-model.trim="$v.formData.sevenClubPrefix.$model"
									:is-valid="!$v.formData.sevenClubPrefix.$error && null"
									:invalid-feedback="getSevenClubPrefixError()"
									:disabled="!hasEditPermission"
									placeholder="e.g. 0012"
									data-test-id="seven-club-prefix-input"
								/>
								<CButton
									:disabled="!formData.sevenClubPrefix"
									class="transparent flex-shrink-0 ml-3"
									color="secondary"
									data-test-id="seven-club-prefix-test-button"
									@click="openPrefixTestDrawer"
								>
									ทดสอบ prefix
								</CButton>
							</div>
							<p class="typo-caption color-black-45 mt-2 mb-0">
								ตัวเลขหรือตัวอักษรก็ได้ ยาวได้ถึง 255 ตัวอักษร ต้องตรงกับที่ตั้งไว้ในหลังบ้าน CRM
							</p>
						</CCol>
					</CRow>
				</div>
			</CCol>
			<CCol md="12" class="mt-4">
				<hr>
			</CCol>
		</CRow>
		<!-- End 7Club+ coupon prefix -->

		<!-- แบบเดิม: สร้างโค้ดในหลังบ้าน — ใช้ไม่ได้เมื่อเปิด 7Club+ prefix -->
		<div class="legacy-coupon-section">
			<div
				v-if="formData.isSevenClubCoupon"
				class="legacy-coupon-section-overlay"
				data-test-id="legacy-coupon-section-overlay"
			>
				<div class="legacy-coupon-section-overlay-message typo-body-2 p-3">
					ใช้ไม่ได้เมื่อเปิด <strong>7Club+ coupon prefix</strong>
					— โค้ดของโปรโมชันนี้มาจากหลังบ้าน CRM ไม่ได้สร้างที่นี่
				</div>
			</div>

			<!-- Usage limit -->
			<CRow class="form-group">
				<CCol md="12">
					<h2 class="typo-h4">
						Usage limit
					</h2>
				</CCol>
				<CCol md="12">
					<label>Usage limit per coupon</label>
				</CCol>
				<CCol md="6" class="mt-2">
					<BaseInputNumber
						v-model="$v.formData.usageLimit.$model"
						:is-valid="!$v.formData.usageLimit.$error"
						:invalid-feedback="$t('global.error.minimumNumber', { number: 1 })"
						:disabled="isDisabledUsageLimit"
						text-align="left"
						placeholder="default is 1 per coupon"
					/>
				</CCol>
			</CRow>
			<!-- End Usage limit -->

			<CRow class="my-4">
				<CCol md="8">
					<h2 class="typo-h4 position-relative">
						Create e-Coupon code
					</h2>
				</CCol>
				<CCol md="4" class="text-right">
					<CInputRadioGroup
						:options="couponModeOptions"
						:custom="true"
						:inline="true"
						:checked="formData.couponMode"
						size="sm"
						class="radio-group"
						name="create-coupon-type"
						@update:checked="handleChangeCouponMode"
					/>
				</CCol>
			</CRow>

			<CRow>
				<CCol>
					<component
						:is="ecouponMode"
						:data-e-coupon="formData"
						:is-edit-mode="isEditMode"
						@update:ecoupon="handleChangeECouponData"
					/>
				</CCol>
			</CRow>

			<CRow>
				<CCol md="12">
					<div class="mt-4">
						<CButton
							:disabled="!isEditMode || isExporting || !hasEditPermission"
							class="transparent"
							color="secondary"
							data-test-id="export-coupon-code"
							@click="exportCouponCode"
						>
							{{ isExporting? 'Exporting...' : 'Export' }}
						</CButton>
						<span v-if="!isEditMode" class="typo-body-2 ml-2">Code can be exported after saves</span>
					</div>
				</CCol>
			</CRow>
		</div>

		<DrawerSevenClubPrefixTest
			:ref="prefixTestDrawerRef"
			:prefix="formData.sevenClubPrefix"
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import {
	required, minValue, maxLength,
} from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

import PromotionECouponManual from '@/components/PromotionECouponManual.vue';
import PromotionECouponAuto from '@/components/PromotionECouponAuto.vue';
import DrawerSevenClubPrefixTest from '@/components/DrawerSevenClubPrefixTest.vue';

import { exportECouponCodeAPI } from '../services/api/export.api';

import {
	PERIOD_STATUSES,
	COUPON_MODES,
	COUPON_MODE_OPTIONS,
	MAXIMUM_SEVEN_CLUB_PREFIX_LENGTH,
} from '../enums/promotions';
import isHavePermission from '../mixins/isHavePermission';


export default {
	name: 'PromotionStepECoupon',

	components: {
		PromotionECouponManual,
		PromotionECouponAuto,
		DrawerSevenClubPrefixTest,
	},

	validations() {
		let schema = {};
		// เปิด 7Club+ prefix = โค้ดมาจากหลังบ้าน CRM ไม่ได้สร้างในหน้านี้
		// จึงไม่ต้องมีโค้ด/จำนวน และไม่ต้องบังคับ format ของโหมด AUTO
		const isSevenClubCoupon = this.formData.isSevenClubCoupon;
		const base = {
			couponCodes: isSevenClubCoupon ? {} : {
				required,
			},
			quantity: isSevenClubCoupon ? {} : {
				required,
				minValue: minValue(1),
			},
			// บังคับ format เฉพาะ "โหมด AUTO ตอนสร้างใหม่" เท่านั้น
			// - MANUAL: ค่าพวกนี้ถูกคำนวณย้อนกลับจากไฟล์ที่ import ไม่ได้ให้ผู้ใช้กรอก
			// - edit mode: ช่องพวกนี้ถูก disable อยู่แล้ว การ require เพิ่มมีแต่จะทำให้
			//   ข้อมูลเก่าที่ไม่ครบ save ไม่ได้ ทั้งที่ผู้ใช้แก้อะไรไม่ได้เลย
			// (เดิมประกาศเป็น {} ทั้งหมด ทำให้ step บอก valid ได้ทั้งที่ค่า format เพี้ยน
			//  เพราะ step ไม่ได้รวม validation ของคอมโพเนนต์ลูกฝั่ง AUTO)
			...(this.isModeAuto && !this.isEditMode && !isSevenClubCoupon ? {
				prefix: {
					required,
				},
				suffix: {
					required,
				},
				length: {
					required,
					minValue: minValue(1),
				},
			} : {
				prefix: {},
				suffix: {},
				length: {},
			}),
			usageLimit: {
				minValue: minValue(1),
			},
			// ปิด toggle = ไม่ต้องมีค่า · เปิด = ต้องมี
			// `v-model.trim` ตัดช่องว่างหน้า-หลังให้แล้ว กรอกเว้นวรรคล้วนจึงเหลือ '' แล้วติด required พอดี
			// ฝั่ง CRM ไม่ได้ฟิกซ์ความยาวไว้ — กันแค่เพดาน 255 ตาม column ของ BE (ไม่งั้นเจอ 422)
			//
			// ⚠️ ต้องประกาศ key คงที่เสมอ แล้วสลับข้างในตัว validator — ห้ามสลับทั้งก้อนเป็น `{}`
			// เพราะ Vue 2 ติดตาม property ที่ "เพิ่งมีทีหลัง" ไม่ได้ — computed ที่อ่าน
			// `$v.formData.sevenClubPrefix.required` ตอนที่ key ยังไม่มี จะค้าง undefined ถาวร
			sevenClubPrefix: {
				// ใช้ `required` ห่อเองแทน `requiredIf` เพราะ `requiredIf` ไม่ trim ให้
				// (เว้นวรรคล้วนจะกลายเป็นผ่าน ทั้งที่ `required` ตัวเต็ม trim ให้)
				required: (value) => !this.formData.isSevenClubCoupon || required(value),
				maxLength: maxLength(MAXIMUM_SEVEN_CLUB_PREFIX_LENGTH),
			},
		};

		schema = {
			formData: {
				...base,
			},
		};

		return schema;
	},

	mixins: [validationMixin, isHavePermission],

	props: {
		defaultData: {
			type: Object,
			default: null,
		},
		isEditMode: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		const {
			id,
			couponMode = COUPON_MODE_OPTIONS[0].value,
			couponCodes = [],
			newCouponCodes = [],
			quantity = null,
			prefix = null,
			suffix = null,
			length = null,
			usageLimit = null,
			isSevenClubCoupon = false,
			sevenClubPrefix = null,
		} = this.defaultData || {};

		return {
			prefixTestDrawerRef: 'drawer-seven-club-prefix-test',
			isExporting: false,
			formData: {
				id,
				couponMode,
				couponCodes,
				newCouponCodes,
				quantity,
				prefix,
				suffix,
				length,
				usageLimit,
				isSevenClubCoupon,
				sevenClubPrefix,
			},
		};
	},

	computed: {
		...mapState('promotions', {
			edit: 'edit',
		}),
		ecouponMode() {
			return this.isModeManual ? PromotionECouponManual : PromotionECouponAuto;
		},
		isModeManual() {
			return this.formData?.couponMode === COUPON_MODES.MANUAL;
		},
		isModeAuto() {
			return this.formData?.couponMode === COUPON_MODES.AUTO;
		},
		isMultiCode() {
			const { couponCodes = [], newCouponCodes = [], quantity = null } = this.formData || {};
			// `codes` จาก API มีแค่ตัวอย่าง (สูงสุด 10) ต้องดู `quantity` ประกอบด้วย
			return Number(quantity) > 1 || couponCodes.length > 1 || newCouponCodes.length > 1;
		},
		isPeriodStatusExpired() {
			return this.periodStatus === PERIOD_STATUSES.EXPIRED;
		},
		isDisabledUsageLimit() {
			if (!this.isEditMode) {
				return false;
			} else if (this.isPeriodStatusExpired) {
				return true;
			}
			return this.isModeAuto || this.isMultiCode;
		},
		periodStatus() {
			const { period_status: periodStatus } = this.edit?.data || {};
			return periodStatus;
		},
		// สลับโหมดหลัง save ไม่ได้ เพราะคูปองที่ออกไปแล้วผูกกับวิธีที่เลือกไว้ตอนสร้าง
		// (ชุดเดียวกับที่ couponMode / prefix-suffix-length ของโหมด AUTO ถูกล็อกไว้ตอน edit)
		isDisabledSevenClubCouponToggle() {
			return !this.hasEditPermission || this.isEditMode;
		},
		couponModeOptions() {
			return COUPON_MODE_OPTIONS.map((option) => ({
				...option,
				...(this.isEditMode ? { props: { disabled: true } } : null), // Disabled option if edit mode.
			}));
		},
	},

	watch: {
		formData: {
			deep: true,
			handler(data) {
				this.$emit(
					'update:is-valid',
					{
						valid: !this.$v.$invalid,
						data,
					},
				);
			},
		},
	},

	methods: {
		// เป็น method ไม่ใช่ computed — `validations()` ของ step นี้สร้าง schema ใหม่ทุกครั้งที่
		// `isSevenClubCoupon` / `couponMode` เปลี่ยน vuelidate จึง rebuild tree ใหม่ทั้งก้อน
		// computed ที่เกาะ dep ไว้กับ tree เก่าจะค้างค่าเดิมถาวร (ขึ้น required ทั้งที่กรอกแล้ว)
		getSevenClubPrefixError() {
			const { required: isRequiredPass, maxLength: isMaxLengthPass } = this.$v.formData.sevenClubPrefix;

			if (!isRequiredPass) {
				return this.$t('global.error.required');
			}
			if (!isMaxLengthPass) {
				return this.$t('global.error.maximumCharacters');
			}

			return null;
		},

		openPrefixTestDrawer() {
			this.$refs[this.prefixTestDrawerRef].open();
		},

		handleChangeCouponMode(value) {
			this.formData.couponMode = value;
			this.formData.couponCodes = [];
			this.formData.quantity = null;
			this.formData.prefix = null;
			this.formData.suffix = null;
			this.formData.length = null;
		},

		handleChangeECouponData(couponData) {
			const {
				couponCodes = [],
				newCouponCodes = [],
				quantity = null,
				prefix = null,
				suffix = null,
				length = null,
			} = couponData;
			this.formData.couponCodes = [...couponCodes];
			this.formData.newCouponCodes = [...newCouponCodes]; // for edit mode, API accept only new updated codes
			this.formData.quantity = quantity;
			this.formData.prefix = prefix;
			this.formData.suffix = suffix;
			this.formData.length = length;
		},

		async exportCouponCode() {
			this.isExporting = true;
			try {
				await exportECouponCodeAPI(this.formData.id);
			} catch (e) {
				// error handled by interceptor
			} finally {
				this.isExporting = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
	.form-container {
		margin-top: rem(80);

		.title-caption {
			position: absolute;
			right: 0;
			bottom: rem(4);
			font-weight: $font-weight-semi-bold;

			span {
				font-weight: $font-weight-bold;
			}
		}
	}

	// ปุ่มทดสอบอยู่บรรทัดเดียวกับช่อง prefix — ถอด margin-bottom ของ form-group ออก
	// เพื่อให้ปุ่มกับ input อยู่ระดับเดียวกันจริง ๆ (ไม่ต้องเดา margin-top ให้เท่าความสูง label)
	// ส่วนคำอธิบายอยู่ใต้ทั้งแถว จะได้ใช้ความกว้างเต็ม
	.prefix-row {
		::v-deep .form-group {
			width: rem(320);
			margin-bottom: 0;
		}
	}

	// ส่วน "สร้างโค้ดในหลังบ้าน" แบบเดิม — เปิด 7Club+ prefix เมื่อไหร่จะถูก overlay ทับ
	// ใช้ overlay แทนการซ่อน เพื่อให้ผู้ใช้ยังเห็นว่ามีอะไรอยู่และรู้ว่าทำไมใช้ไม่ได้
	.legacy-coupon-section {
		position: relative;

		&-overlay {
			position: absolute;
			top: rem(-8);
			right: rem(-8);
			bottom: rem(-8);
			left: rem(-8);
			z-index: 2;

			background-color: rgba($color-white, 0.75);
			cursor: not-allowed;

			&-message {
				max-width: rem(520);
				margin: rem(24) auto 0;

				border: 1px solid $color-gray-300;
				border-radius: rem(4);
				background-color: $color-white;
				color: $color-black-70;
				text-align: center;
			}
		}
	}

	.prefix-test-result {
		border-radius: rem(4);
		border-left: rem(3) solid $color-gray-400;
		background-color: $color-white;

		&.is-pass {
			border-left-color: $color-success;
		}

		&.is-warning {
			border-left-color: $color-alert;
		}
	}
</style>
